import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { ArrowLeft, Lock } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { AccessLogs } from "@/components/Entries/AccessLogs";
import { useAuth } from "@/contexts/AuthContext";
import { useEntryTracking } from "@/hooks/useEntryTracking";

type Entry = Database["public"]["Tables"]["entries"]["Row"];

const SharedEntry = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState<Entry | null>(null);
  const [password, setPassword] = useState("");
  const [isProtected, setIsProtected] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Use entry tracking hook
  useEntryTracking(entry?.id || "");

  useEffect(() => {
    if (!shareId) return;

    const fetchEntry = async () => {
      setLoading(true);

      const { data, error } = await supabase
        .from("entries")
        .select("*")
        .eq("share_id", shareId)
        .eq("is_public", true)
        .single();

      if (error) {
        toast.error("This entry doesn't exist or isn't shared publicly");
        navigate("/");
        return;
      }

      setEntry(data);
      setIsProtected(Boolean(data.password));
      setIsUnlocked(!data.password);
      setLoading(false);
    };

    fetchEntry();
  }, [shareId, navigate]);

  const verifyPassword = async () => {
    if (!entry) return;
    
    setVerifying(true);
    
    try {
      const { data, error } = await supabase
        .rpc('check_entry_password', {
          entry_id: entry.id,
          provided_password: password
        });
        
      if (error) throw error;
      
      if (data === true) {
        setIsUnlocked(true);
      } else {
        toast.error("Invalid password");
      }
    } catch (error: any) {
      toast.error("Failed to verify password");
      console.error("Error verifying password:", error);
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-4 w-32 mb-8" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4 mb-8" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 text-center">
        <h3 className="text-xl font-medium mb-2">Entry not found</h3>
        <p className="text-muted-foreground mb-4">
          This shared entry may have been deleted or the link is invalid
        </p>
        <Button onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Home
        </Button>
      </div>
    );
  }

  if (isProtected && !isUnlocked) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 flex flex-col items-center">
        <Card className="w-full">
          <CardHeader className="text-center">
            <Lock className="mx-auto h-12 w-12 text-muted-foreground" />
            <h2 className="text-2xl font-bold mt-4">Password Protected</h2>
            <p className="text-muted-foreground">
              This entry is protected. Enter the password to view it.
            </p>
          </CardHeader>
          <CardContent>
            <Input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mb-2"
              onKeyDown={(e) => e.key === "Enter" && verifyPassword()}
            />
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full" 
              onClick={verifyPassword}
              disabled={verifying || !password}
            >
              {verifying ? "Verifying..." : "Submit"}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">{entry.title}</h1>
      <div className="text-sm text-muted-foreground mb-8">
        {new Date(entry.updated_at).toLocaleDateString()}
        {' · '}
        {entry.entry_type}
      </div>

      <div 
        className="entry-content"
        dangerouslySetInnerHTML={{ __html: entry.content || "<p><em>No content</em></p>" }}
      />

      {/* Access logs for public entries */}
      <AccessLogs 
        entryId={entry.id} 
        isCreator={false} 
        isPublic={true} 
      />
      
      <div className="mt-8 pt-4 border-t">
        <p className="text-sm text-muted-foreground mb-2">
          Viewing a shared entry from Brain - Your Personal Knowledge Base
        </p>
        <Button variant="outline" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go to Brain
        </Button>
      </div>
    </div>
  );
};

export default SharedEntry;
